// AppShell (DESIGN §2): Sidebar | main column (TopBar + page) | right panel (rendered by <Page aside>) + BottomNav
// below 768px, plus the global hosts (modals/drawers, search palette, toasts).
import { useEffect } from 'preact/hooks';
import { useStore } from '../store.js';
import { SearchPalette } from './search.jsx';
import { BottomNav, Sidebar } from './sidebar.jsx';
import { TopBar, documentTitle } from './topbar.jsx';
import { Button, ModalHost, ToastHost } from './ui.jsx';
import { Icon } from '../icons.jsx';

// Still on a password someone else set (the team's initial password or an admin's reset): anyone who knows it can
// sign in as this person, so every page asks for their own until they change it (Settings shows the form itself).
function TemporaryPasswordBar({ route }) {
  const { user } = useStore();
  if (!user?.temporaryPassword || route.name === 'settings') return null;
  return (
    <div class="temp-pw" role="status">
      <Icon name="alert" size={16} class="temp-pw-icon" />
      <p class="temp-pw-text">
        <span class="hide-sm">You’re using a temporary password. Choose your own so only you can sign in as {user.name.split(' ')[0]}.</span>
        <span class="show-sm">You’re using a temporary password.</span>
      </p>
      <Button size="sm" variant="secondary" href="#/settings" aria-label="Change password">
        <span class="hide-sm">Change password</span>
        <span class="show-sm">Change</span>
      </Button>
    </div>
  );
}

// The page a route shows: a new page scrolls to the top; query changes (filters, calendar dates, month switches in
// the Workspace) keep the scroll position.
const pageKey = (route) => `${route.name}:${route.params.batchId || route.params.memberId || ''}`;

export function AppShell({ route, children }) {
  const { index } = useStore();
  const key = pageKey(route);

  useEffect(() => { window.scrollTo(0, 0); }, [key]);
  const title = documentTitle(route, index);
  useEffect(() => { document.title = title; }, [title]);

  const skip = (e) => {
    e.preventDefault();
    document.getElementById('main')?.focus();
  };

  return (
    <div class="app">
      <a class="skip-link" href="#main" onClick={skip}>Skip to content</a>
      <Sidebar route={route} />
      <div class="main-col">
        <TopBar route={route} />
        <main class="main" id="main" tabIndex={-1}>
          <TemporaryPasswordBar route={route} />
          {children}
        </main>
      </div>
      <BottomNav route={route} />
      <ModalHost />
      <SearchPalette />
      <ToastHost />
    </div>
  );
}
